<template>
  <v-dialog v-model="dialog" scrollable persistent max-width="500px" transition="dialog-transition">
    <v-card>
      <v-icon v-if="closable" class="close-icon" @click="$emit('close')">mdi-close</v-icon>
      <v-card-title primary-title class="mb-3">
        Confirme seus dados
      </v-card-title>
      <v-card-text class="scroll-card">
        <div v-if="loading" class="d-flex justify-center align-center">
          <v-progress-circular :value="20" indeterminate></v-progress-circular>
        </div>
        <v-form ref="form">
          <v-row v-if="user">
            <v-col cols="12" class="my-0 py-0">
              Seu nome
              <v-text-field
                v-model="user.nome"
                type="text"
                solo
                flat
                dense
                placeholder="Digite aqui seu nome"
                class="custom-textfield mt-1"
                :rules="[
                  () => $v.user.nome.required || 'Campo obrigatório',
                  () => $v.user.nome.minLength || 'Mínimo de 6 caracteres'
                ]"
              />
            </v-col>
            <v-col cols="12" class="my-0 py-0">
              Seu email
              <v-text-field
                v-model="user.email"
                type="email"
                solo
                flat
                dense
                placeholder="Digite aqui seu e-mail"
                class="custom-textfield mt-1"
                :rules="[
                  () => $v.user.email.required || 'Campo obrigatório',
                  () => $v.user.email.email || 'E-mail inválido'
                ]"
              />
            </v-col>
            <v-col cols="12" class="my-0 py-0">
              Seu CPF
              <v-text-field
                v-model="user.cpf"
                type="text"
                solo
                flat
                dense
                v-mask="'###.###.###-##'"
                placeholder="Digite aqui seu CPF"
                class="custom-textfield mt-1"
                :rules="[
                  () => $v.user.cpf.required || 'Campo obrigatório',
                  () => $v.user.cpf.minLength || 'CPF inválido',
                  v => validaCpf(v) || 'CPF inválido'
                ]"
              />
            </v-col>
            <v-col cols="12" class="my-0 py-0">
              Sua data de nascimento
              <v-text-field
                solo
                flat
                placeholder="Digite sua data de nascimento"
                v-model="user.dtNascimento"
                class="custom-textfield mt-1"
                v-mask="'##/##/####'"
                :rules="[
                  () => $v.user.dtNascimento.required || 'Campo obrigatório',
                  () => $v.user.dtNascimento.minimal || 'O funcionário tem que ter pelo menos 14 anos'
                ]"
              />
            </v-col>
            <v-col class="justify-center text-center">
              <v-btn color="primary" @click="confirmDialog = true" :loading="loadingUpdate" :disabled="loadingUpdate">
                Confirmar dados
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
    <ConfirmDialog
      v-if="confirmDialog"
      v-model="confirmDialog"
      title="Deseja confirmar os dados?"
      text="Queremos assegurar que as suas informações estejam corretas para evitar qualquer problema nos seus documentos"
      cancelButtonText="Revisar dados"
      confirmButtonText="Confirmar"
      @on-confirm="updateUserData"
    />
  </v-dialog>
</template>
<script>
import userService from '@/services/user'
import { required, minLength, email, maxLength } from 'vuelidate/lib/validators'
import validaCpf from '@/utils/validaCpf'
import ConfirmDialog from '@/core/components/ConfirmDialog'
import { validationMixin } from 'vuelidate'
import { dateRegex } from '@/utils/constants'
import { formatDate, extractOnlyNumbers } from '@/utils/formatter'
import minimalDate from '../../utils/minimalDate'
import { parse } from 'date-fns'

export default {
  props: {
    closable: {
      required: false,
      default: false,
      type: Boolean
    }
  },
  mixins: [validationMixin],
  validations: {
    user: {
      nome: {
        required,
        minLength: minLength(6)
      },
      cpf: {
        required,
        minLength: minLength(14)
      },
      email: {
        required,
        email
      },
      dtNascimento: {
        required,
        validDate(dtNascimento) {
          if (dtNascimento) {
            const [day, month, year] = dtNascimento.split('/')
            return dateRegex.test(dtNascimento) && new Date(year, month - 1, day) < new Date(Date.now())
          }
          return false
        },
        minimal(dt_nascimento) {
          const dataNascimento = parse(dt_nascimento, 'dd/MM/yyyy', new Date())
          return minimalDate(dataNascimento, 14)
        }
      },
      funcionario: {
        ctps: { maxLength: maxLength(20) },
        serie_ctps: { maxLength: maxLength(10) },
        pis_pasep: { minLength: minLength(14) }
      }
    }
  },
  components: {
    ConfirmDialog
  },
  data() {
    return {
      user: null,
      dialog: true,
      loading: false,
      confirmDialog: false,
      loadingUpdate: false
    }
  },
  methods: {
    validaCpf(cpf) {
      if (process.env.VUE_APP_ENV === 'DEV') return true
      return validaCpf(cpf)
    },
    async getUserData() {
      try {
        this.loading = true
        const { data } = await userService.showDadosBasicos()
        this.user = { ...data, dtNascimento: new Date(data.dtNascimento).toLocaleDateString('pt-br') }
      } catch (error) {
        console.log(error)
      } finally {
        this.loading = false
      }
    },
    async updateUserData() {
      if (this.$refs.form.validate()) {
        try {
          this.loadingUpdate = true
          const params = {
            ...this.user,
            dtNascimento: formatDate(this.user.dtNascimento),
            cpf: extractOnlyNumbers(this.user.cpf)
          }
          await userService.updateDadosBasicos(params)
          this.$showSuccessMessage('Dados confirmados')
          this.$emit('dados-confirmados')
        } catch ({ response }) {
          this.$showErrorMessage(response.data.message)
        } finally {
          this.loadingUpdate = false
        }
      }
    }
  },
  mounted() {
    this.getUserData()
  }
}
</script>
<style scoped>
.scroll-card {
  overflow-y: scroll;
}
.close-icon {
  position: absolute;
  right: 0;
  margin-top: 10px;
  margin-right: 10px;
}
</style>
